import React, { useState, useEffect, useRef } from "react"
import PubSub from "pubsub-js"
import { api } from "aldoo-ra/API"
import { priceDisplayFormat } from "aldoo-ra/CMSModels/StorePrice/display-formatters"
import ExpressionParser from "aldoo-ra/ExpressionParser"
import Localization from "aldoo-ra/Localization"
import CMSData from "aldoo-ra/CMS/cms-data"
import { BulkGetCMSContent } from "aldoo-ra/CMS/api"
import { url } from "aldoo-ra/CMS/url"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import parse from "html-react-parser"
import { store } from "aldoo-ra/Store"
import ContentDistancer from "./ContentDistancer"

const StoreAPI = api("store")

const PartnerTooltipContent = ({ product }) => (
  <div className="flex flex-col min-w-[160px] max-w-full">
    <img
      src={url(product.logo)}
      alt={product.name}
      className="w-20 lg:w-[115px] h-20 lg:h-[115px]"
    />

    {product.tooltip_title && (
      <Typography
        variant="body_20_accent"
        className="text-text leading-tight mt-0 mb-4"
      >
        {product.tooltip_title}
      </Typography>
    )}

    {product.tooltip_description && (() => {
      const desc = Array.isArray(product.tooltip_description)
        ? product.tooltip_description
        : [product.tooltip_description]
      return desc.map((text, index) => (
        <Typography
          key={index}
          component={"div"}
          variant="info_16"
          className="w-full text-text my-0"
        >
          {parse(text)}
        </Typography>
      ))
    })()}
  </div>
)

const InfoIcon = () => (
  <div className="w-[18px] h-[18px] rounded-full border border-primary text-primary bg-paper flex items-center justify-center text-[11px] font-semibold leading-none cursor-default select-none">
    i
  </div>
)

const ProductCard = ({
  product,
  cardClassName,
  logoClassName,
  featureClassName,
  featureContainerClassName,
  platformContainerClassName,
  platformIconClassName,
  onCardClick,
}) => {
  const isInteractive = product.name !== "Aqua Mail"

  return (
    <div
      className={`${cardClassName}${isInteractive ? " cursor-pointer transition-all duration-200 hover:scale-105" : ""}`}
      onClick={isInteractive && onCardClick ? () => onCardClick(product) : undefined}
    >
      {isInteractive &&  (product.use_overlay_info == undefined || product.use_overlay_info === true) && (
        <div className="absolute top-2 right-3 z-5">
          <InfoIcon />
        </div>
      )}

      <img
        src={url(product.logo)}
        alt={product.name || product.label}
        className={logoClassName}
      />

      {product.featureimage && (
        <img
          src={url(product.featureimage)}
          alt={product.name}
          className="w-full object-contain mb-0 mt-1 md:mt-3"
        />
      )}

      {Array.isArray(product.features) && product.features.length > 0 && (
        <div className={featureContainerClassName}>
          {product.features.map((feature, i) => (
            <div
              className={featureClassName}
              key={i}
              dangerouslySetInnerHTML={{ __html: feature }}
            />
          ))}
        </div>
      )}

      {product.platforms && product.platforms.length > 0 && (
        <div className={platformContainerClassName}>
          {product.platforms.map((platform, i) => (
            <img
              key={i}
              src={url(platform.image)}
              alt={platform.label}
              className={platformIconClassName}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const BundleCardOverlay = ({ product, onClose, closeText }) => {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)))
  }, [])

  const handleClose = () => {
    setVisible(false)
    setTimeout(onClose, 300)
  }

  return (
    <div
      className={`absolute inset-0 z-20 bg-paper rounded-[24px] flex flex-col transition-transform duration-300 ease-in-out ${visible ? "translate-y-0" : "translate-y-full"}`}
      onClick={handleClose}
    >
      <div className="flex-1 overflow-hidden px-4 md:px-6 xl:px-10 py-2 scrollbar-none">
        <PartnerTooltipContent product={product} />
      </div>

      <div className="shrink-0 py-2 text-center">
        <Typography variant="info_15" className="text-gray_2 my-0">
          {closeText}
        </Typography>
      </div>
    </div>
  )
}

const MobileBundleCard = ({ bundle, handleAddToCart, closeText }) => {
  const [overlayProduct, setOverlayProduct] = useState(null)

  return (
    <div className="relative isolate w-full max-w-[345px] md:max-w-[460px] flex flex-col justify-between items-center text-text bg-theme_white rounded-[24px] shadow-custom_card px-6 md:px-10 pt-10 pb-6 overflow-hidden mx-auto">
      {/* Promo Features Badge */}
      {bundle.promo_features && bundle.promo_features.length > 0 && (
        <div className="absolute top-0 right-0 flex bg-primary_lt rounded-bl-[18px] gap-[6px] px-4 py-[6px]">
          {bundle.promo_features.map((feature, index) => {
            const label = feature.title || feature.name || feature.label
            return (
              <div key={index} className="flex items-center gap-1">
                {feature.image && (
                  <img
                    src={url(feature.image)}
                    alt={label}
                    className="w-4 h-4"
                  />
                )}
                <Typography
                  variant="info_15"
                  className="text-gray_1 whitespace-nowrap my-0"
                >
                  {label}
                </Typography>
              </div>
            )
          })}
        </div>
      )}

      {/* Title, Platforms, Price and Discount */}
      <div className="w-full flex items-start justify-between mb-[13px] md:mb-5">
        <div>
          <Typography
            component="h2"
            variant="h3_accent"
            className="w-[160px] mb-2"
          >
            {bundle.name.split("|").map((item, index) => (
              <span
                key={index}
                className={index === 0 ? "text-primary" : "text-text"}
              >
                {item}
              </span>
            ))}
          </Typography>
        </div>

        <div className="flex flex-col items-end gap-[6px] md:mt-1">
          <Typography variant="h4" className="my-0">
            {parse(bundle.price)}
          </Typography>

          <Typography
            variant="info_16"
            className="text-text text-right whitespace-nowrap my-0"
          >
            {parse(bundle.billed_as)}
          </Typography>

          {bundle.seats && (
            <Typography
              variant="info_16"
              className="text-text text-right whitespace-nowrap my-0"
            >
              {parse(bundle.seats)}
            </Typography>
          )}

          {bundle.discount && (
            <div className="h-[22px] flex items-center justify-center bg-primary_alt text-white rounded-md px-[7px] mb-1 text-sm">
              <Typography variant="info_17_medium" className="uppercase">
                {bundle.discount}
              </Typography>
            </div>
          )}
        </div>
      </div>

      {/* Bundle Products */}
      <div className="w-full flex flex-col">
        <div className="grid grid-cols-2 gap-4 w-full">
          {bundle.products?.map((product, index) => (
            <ProductCard
              key={index}
              product={product}
              onCardClick={setOverlayProduct}
              cardClassName="relative w-[140px] md:w-[180px] h-[140px] md:h-[180px] flex flex-col items-center border border-primary_hov rounded-[17px] pb-4 pt-2 md:pt-4 px-1 text-center"
              logoClassName="w-auto h-[72px]"
              featureClassName="text-text text-[12px] md:text-[14px] font-light my-0"
              featureContainerClassName="mt-1 md:mt-3"
              platformContainerClassName="absolute h-4 -bottom-2 flex space-x-2 bg-paper px-2 mt-2 z-10"
              platformIconClassName="w-4 h-4"
            />
          ))}
        </div>
      </div>

      {/* CTA Button */}
      <Button
        variant="primary_l"
        className="mt-4 md:mt-5"
        onClick={handleAddToCart}
      >
        {bundle.cta}
      </Button>

      {overlayProduct && (overlayProduct.use_overlay_info == undefined || overlayProduct.use_overlay_info === true) && (
        <BundleCardOverlay
          product={overlayProduct}
          onClose={() => setOverlayProduct(null)}
          closeText={closeText}
        />
      )}
    </div>
  )
}

const DesktopBundleCard = ({ bundle, handleAddToCart, closeText }) => {
  const isAquaMailUltimatePack = bundle.name === "Aqua Mail | Ultimate Pack"
  const [overlayProduct, setOverlayProduct] = useState(null)

  return (
    <div className="relative isolate w-full flex flex-col justify-between items-start text-text bg-theme_white rounded-[24px] shadow-custom_card lg:px-[40px] xl:px-[58px] lg:py-[60px] xl:py-[75px] overflow-hidden">
      {/* Promo Features Badge */}
      {bundle.promo_features && bundle.promo_features.length > 0 && (
        <div className="absolute top-0 right-0 flex flex-col bg-primary_lt rounded-bl-[24px] gap-[6px] px-3 py-3">
          {bundle.promo_features.map((feature, index) => {
            const label = feature.title || feature.name || feature.label
            return (
              <div key={index} className="flex items-center space-x-2">
                {feature.image && (
                  <img
                    src={url(feature.image)}
                    alt={label}
                    className="w-4 h-4"
                  />
                )}
                <Typography variant="info_15" className="text-gray_1 my-0">
                  {label}
                </Typography>
              </div>
            )
          })}
        </div>
      )}

      {/* Title */}
      <Typography component="h2" variant="h3_accent" className="w-full mb-5">
        {bundle.name.split("|").map((item, index) => (
          <span
            key={index}
            className={index === 0 ? "text-primary" : "text-text"}
          >
            {item}
          </span>
        ))}
      </Typography>

      {/* Card content section */}
      <div
        className={`w-full flex ${
          isAquaMailUltimatePack ? "items-center" : "items-end"
        } justify-between`}
      >
        {/* Text Column */}
        <div className="flex flex-col flex-1 items-start justify-start text-left">
          {/* Discount Label */}
          {bundle.discount && (
            <div className="h-[34px] flex items-center justify-center bg-primary_alt text-white rounded-md px-[10px] mb-1 text-sm">
              <Typography variant="info_17_medium" className="uppercase">
                {bundle.discount}
              </Typography>
            </div>
          )}

          {/* Bundle Price */}
          <Typography variant="h4" className="my-0">
            {parse(bundle.price)}
          </Typography>

          <Typography variant="info_16" className="text-text mb-0 mt-0">
            {parse(bundle.billed_as)}
          </Typography>

          {bundle.seats && (
            <Typography variant="info_16" className="text-text mt-1 mb-0">
              {parse(bundle.seats)}
            </Typography>
          )}

          {/* CTA Button */}
          <Button
            variant="primary_l"
            className={isAquaMailUltimatePack ? "mt-8 mb-[78px]" : "mt-3 xl:mt-4"}
            onClick={handleAddToCart}
          >
            {bundle.cta}
          </Button>
        </div>

        {/* Bundle Products */}
        <div className="grid grid-cols-2 gap-5 justify-end w-full lg:w-auto">
          {bundle.products?.map((product, index) => (
            <ProductCard
              key={index}
              product={product}
              onCardClick={setOverlayProduct}
              cardClassName="relative lg:w-[210px] xl:w-[222px] lg:h-[210px] xl:h-[222px] flex flex-col items-center border border-primary_hov rounded-[27px] pb-[25px] pt-[17px] px-[18px] text-center"
              logoClassName="w-auto h-[115px] mb-0"
              featureClassName="text-text lg:text-[16px] xl:text-[18px] font-light my-0"
              featureContainerClassName=""
              platformContainerClassName="absolute h-5 -bottom-[10px] flex space-x-2 bg-paper px-2 mt-2 z-10"
              platformIconClassName="w-5 h-5"
            />
          ))}
        </div>
      </div>

      {overlayProduct && (overlayProduct.use_overlay_info == undefined || overlayProduct.use_overlay_info === true) && (
        <BundleCardOverlay
          product={overlayProduct}
          onClose={() => setOverlayProduct(null)}
          closeText={closeText}
        />
      )}
    </div>
  )
}

const BundleCards = ({ onBundlesUpdate }) => {
  const [isClient, setIsClient] = useState(false)
  const [bundles, setBundles] = useState([])
  const [closeText, setCloseText] = useState("")
  const prevKey = useRef("")
  const { lt } = Localization()

  useEffect(() => {
    setIsClient(true)
  }, [])

  // Get the bundle cards data from CMS
  const { data, loading, error } = CMSData({
    contentType: "BundleCardGroup",
    match: { "content.id": "bundle_card_group" },
  })

  // Fetch full BundleProductCard documents to get tooltip fields missing from embedded data
  const { data: productCardsData } = CMSData({
    contentType: "BundleProductCard",
  })

  // Process bundles, merge fresh product/bundle CMS data, then set state once.
  // Single setBundles call per data change — no bundles in deps, no update loop.
  useEffect(() => {
    if (!data?.length) return

    const guardKey =
      JSON.stringify(data) +
      "|" +
      (productCardsData?.map((p) => p._id).join(",") ?? "")
    if (guardKey === prevKey.current) return
    prevKey.current = guardKey

    const process = async () => {
      try {
        // Clone to avoid mutating the CMS cache objects
        const bundleCards = JSON.parse(JSON.stringify(data[0]?.cards))
        if (!bundleCards || !Array.isArray(bundleCards)) {
          console.warn("No valid bundle cards found")
          return
        }

        const productIDs = bundleCards
          .map((bundle) => bundle.product?.item)
          .filter(Boolean)

        const result = await StoreAPI({
          resolve_price: {
            products: productIDs,
            platform: "Web",
          },
        })

        let processedBundles = await Promise.all(
          bundleCards.map(async (bundle) => {
            const product = result.find(
              (p) => p.product_id === bundle.product?.item
            )

            if (!product) return bundle

            if (product.amount) {
              let price = ExpressionParser.evaluate(bundle.price, { product })

              bundle.price =
                priceDisplayFormat(price, product.currency) +
                `<span style="fontSize:27px; fontWeight: normal">${bundle.duration}</span>`

              const evalClone = { ...product }
              evalClone.amount = priceDisplayFormat(
                product.amount,
                product.currency
              )
              bundle.billed_as = ExpressionParser.evaluate(bundle.billed_as, {
                product: evalClone,
              })
            }

            //Proveriavame dali ima bundle.seats template kato: "{{product.seats}} user" i dali ima .seats ili .licenseModel.seats
            //v edno ot dvete trqbwa da ima neshto..
            if (bundle.seats && (product.seats || product.licenseModel.seats)) {
              //tuk kazwa ako ima product.seats go polzwai, ako ne vzemi ot license
              product.seats ??= product.licenseModel?.seats
              //tuk zamenq {{product.seats}}
              bundle.seats = ExpressionParser.evaluate(bundle.seats, { product })
            }

            product.name && (product.name = await lt(product.name))
            product.description &&
              (product.description = await lt(product.description))

            bundle.product = product
            return bundle
          })
        )

        // Merge fresh BundleProductCard data — overrides stale embedded product snapshots
        if (productCardsData?.length) {
          processedBundles = processedBundles.map((bundle) => ({
            ...bundle,
            products: bundle.products?.map((p) => {
              const full = productCardsData.find((pc) => pc._id === p._id)
              return full ? { ...p, ...full } : p
            }),
          }))
        }

        // Resolve thin-ref promo_features {_id, label} → full {title, image} via bulk fetch
        const thinRefIds = [
          ...new Set(
            processedBundles
              .flatMap((b) => b.promo_features || [])
              .filter((pf) => pf._id && !pf.title && !pf.image)
              .map((pf) => pf._id)
          ),
        ]
        if (thinRefIds.length > 0) {
          const iconsResponse = await BulkGetCMSContent({
            payload: { ids: thinRefIds, expand: true },
          })
          if (iconsResponse?.result && iconsResponse.data?.length) {
            const iconsMap = Object.fromEntries(
              iconsResponse.data.map((item) => [item._id, item.content ?? item])
            )
            processedBundles = processedBundles.map((bundle) => ({
              ...bundle,
              promo_features: bundle.promo_features?.map((pf) => {
                if (pf.title && pf.image) return pf
                const full = iconsMap[pf._id]
                return full ? { ...pf, ...full } : pf
              }),
            }))
          }
        }

        setCloseText(data[0]?.click_to_close_text || "")
        setBundles(processedBundles)
      } catch (error) {
        console.error("Error processing bundles:", error)
      }
    }

    process()
  }, [data, lt, productCardsData])

  const viewItemListFiredRef = useRef(false)

  // GA4 view_item_list — Bundles. Mirrors the Plans list event so the funnel
  // (view_item_list → view_item → add_to_cart → begin_checkout → purchase)
  // has a proper bundles entry point.
  useEffect(() => {
    if (viewItemListFiredRef.current) return
    if (!bundles?.length) return
    const items = bundles
      .filter((b) => b.product?.product_id || b.product?.item)
      .map((b) => ({
        item_id: b.product?.product_id || b.product?.item,
        item_name: b.product?.name || b.product?.product_id,
        item_list_name: "Bundles",
        price: Number(b.product?.amount) || 0,
        quantity: 1,
      }))
    if (!items.length) return
    const currency = bundles.find((b) => b.product?.currency)?.product?.currency
    // Defer one tick so GoogleTracking (parent useEffect) has subscribed.
    // React fires child effects before parent's — without this, the publish
    // happens before the subscriber registers and the event is dropped.
    setTimeout(() => {
      PubSub.publish("Tracking_ViewItemList", {
        items,
        currency,
        itemListName: "Bundles",
      })
    }, 0)
    viewItemListFiredRef.current = true
  }, [bundles])

  // Notify parent component about available bundles
  useEffect(() => {
    if (onBundlesUpdate) {
      const availableBundles = bundles.filter((bundle) => {
        // Hide bundles without product data (likely misconfigured in CMS)
        if (!bundle.product) return false
        return bundle.product.in_stock !== false && bundle.product.available !== false
      })
      onBundlesUpdate(availableBundles.length > 0)
    }
  }, [bundles, onBundlesUpdate])

  if (!isClient) return null
  if (loading || error) return null

  const handleAddToCart = (bundle) => {
    if (!bundle.product) return
    PubSub.publish("Tracking_ViewItem", {
      currency: bundle.product?.currency,
      value: Number(bundle.product?.amount) || 0,
      items: [
        {
          item_id: bundle.product?.product_id || bundle.product?.item,
          item_name: bundle.product?.name || bundle.product?.product_id,
          item_list_name: "Bundles",
          price: Number(bundle.product?.amount) || 0,
          quantity: 1,
        },
      ],
    })
    store.addToCart({
      product: bundle.product,
      proceedToCart: true,
    })
  }

  const availableBundles = bundles.filter((bundle) => {
    // Hide bundles without product data (likely misconfigured in CMS)
    if (!bundle.product) return false
    return bundle.product.in_stock !== false && bundle.product.available !== false
  })

  return (
    <>
      {/* Mobile Layout */}
      <div className="flex flex-col lg:hidden w-[330px] md:w-[720px] gap-10 pt-[88px] mx-auto">
        {availableBundles.map((bundle, idx) => (
          <MobileBundleCard
            key={idx}
            bundle={bundle}
            handleAddToCart={() => handleAddToCart(bundle)}
            closeText={closeText}
          />
        ))}
      </div>

      {/* Desktop Layout */}
      <div className="flex">
        <div className="hidden lg:flex flex-col lg:w-[720px] xl:w-[828px] lg:mt-0 lg:pt-[88px] xl:pt-0 mx-auto gap-10">
          {availableBundles.map((bundle, idx) => (
            <DesktopBundleCard
              key={idx}
              bundle={bundle}
              handleAddToCart={() => handleAddToCart(bundle)}
              closeText={closeText}
            />
          ))}
        </div>

        {/* Invisible div for distancing the side menu from the cards */}
        <ContentDistancer height="500px" />
      </div>
    </>
  )
}

const AldooComponent = {
  id: 'BundleCards',
  component: BundleCards,
  input: {},
  meta: {
    name: 'Bundle Cards',
    description: 'Product bundle pricing cards with add to cart functionality',
    category: 'Marketing',
  },
}

export { AldooComponent }
export default BundleCards